import { FlashcardViewer } from '@/components/learning/flashcard-viewer'
import { QAPanel } from '@/components/learning/qa-panel'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { bookCategoryColors, bookCategoryLabels, bookModuleTypeLabels } from '@/constants/learning'
import { useBook, useModule, usePersonalizeBook } from '@/hooks/use-learning'
import type { LearningModuleWithProgress } from '@/types'
import {
  AlertCircle,
  ArrowLeft,
  BookOpen,
  ChevronRight,
  Clock,
  Loader2,
  Sparkles,
  Star,
  User,
} from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import Markdown from 'react-markdown'
import { Link, useParams } from 'react-router-dom'
import remarkGfm from 'remark-gfm'

function BookCover({ coverUrl, title }: { coverUrl: string | null; title: string }) {
  if (!coverUrl) {
    return (
      <div className="w-32 h-48 bg-muted rounded-lg flex items-center justify-center flex-shrink-0">
        <BookOpen className="h-12 w-12 text-muted-foreground" />
      </div>
    )
  }

  return (
    <img
      src={coverUrl}
      alt={`Cover of ${title}`}
      className="w-32 h-48 object-cover rounded-lg flex-shrink-0 shadow-md"
      onError={(e) => {
        const target = e.target as HTMLImageElement
        target.style.display = 'none'
      }}
    />
  )
}

function ModuleContent({ moduleData }: { moduleData: LearningModuleWithProgress }) {
  const { data: fullModule, isLoading } = useModule(moduleData.id)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!fullModule?.text_content) {
    return (
      <div className="py-8 text-center text-muted-foreground">
        Content not yet generated. Check back later.
      </div>
    )
  }

  // Render flashcards interactively if this is a flashcard module
  if (moduleData.module_type === 'flashcards') {
    return (
      <div className="space-y-6">
        <FlashcardViewer content={fullModule.text_content} />
        <details className="border rounded-lg">
          <summary className="px-4 py-2 text-sm text-muted-foreground cursor-pointer hover:text-foreground">
            View as text
          </summary>
          <div className="px-4 pb-4 prose prose-sm dark:prose-invert max-w-none">
            <Markdown remarkPlugins={[remarkGfm]}>
              {fullModule.text_content.replace(/<!--[\s\S]*?-->/g, '')}
            </Markdown>
          </div>
        </details>
      </div>
    )
  }

  return (
    <div className="prose prose-sm dark:prose-invert max-w-none">
      <Markdown remarkPlugins={[remarkGfm]}>{fullModule.text_content}</Markdown>
    </div>
  )
}

export function LearnBookPage() {
  const { bookId } = useParams<{ bookId: string }>()
  const parsedBookId = bookId ? Number(bookId) : Number.NaN
  const validBookId = Number.isFinite(parsedBookId) ? parsedBookId : Number.NaN
  const { data: book, isLoading, isError, refetch } = useBook(validBookId)
  const personalize = usePersonalizeBook()
  const [expandedModule, setExpandedModule] = useState<number | null>(null)
  const [qaOpen, setQaOpen] = useState(false)
  const [qaModuleId, setQaModuleId] = useState<number | null>(null)

  // Detect if personalization is in progress (from server state or mutation)
  const isGenerating =
    personalize.isPending ||
    book?.personalized_generation_status === 'pending' ||
    book?.personalized_generation_status === 'generating'

  // For Q&A: use the module being asked about, or find one from current view
  const currentViewModule =
    expandedModule != null
      ? book?.modules?.find((m) => m.id === expandedModule)
      : book?.personalized_module

  // Track which module the Q&A is for (persists when module is collapsed)
  const findModuleById = (id: number) =>
    book?.modules?.find((m) => m.id === id) ??
    (book?.personalized_module?.id === id ? book.personalized_module : undefined)

  const activeModuleForQA = qaModuleId != null ? findModuleById(qaModuleId) : currentViewModule

  // Update QA module when user expands a module or views personalized content
  useEffect(() => {
    if (currentViewModule) {
      setQaModuleId(currentViewModule.id)
    }
  }, [currentViewModule])

  const getScrollPercent = useCallback(() => 0, []) // No scroll tracking for book modules

  const handlePersonalize = () => {
    if (!validBookId) return
    personalize.mutate(validBookId, {
      onSuccess: () => {
        refetch()
      },
    })
  }

  // SSE events automatically refetch when topic generation status changes

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (isError || !book) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="w-full px-6 py-8">
          <Link to="/learn/books">
            <Button variant="ghost" size="sm" className="mb-4 -ml-2 gap-1">
              <ArrowLeft className="h-4 w-4" />
              Back to Books
            </Button>
          </Link>
          <Card>
            <CardContent className="py-12 text-center">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 text-destructive" />
              <p className="text-muted-foreground">Book not found.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  const allModules = [...(book.modules || [])]

  return (
    <div className="h-full overflow-y-auto">
      <div className="w-full px-6 py-8 max-w-4xl mx-auto">
        {/* Back navigation */}
        <Link to="/learn/books">
          <Button variant="ghost" size="sm" className="mb-4 -ml-2 gap-1">
            <ArrowLeft className="h-4 w-4" />
            Back to Books
          </Button>
        </Link>

        {/* Book Header */}
        <div className="flex gap-6 mb-8">
          <BookCover coverUrl={book.cover_url} title={book.title} />
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold mb-1">{book.title}</h1>
            <p className="text-muted-foreground mb-3">{book.author}</p>

            <div className="flex items-center gap-3 flex-wrap mb-3">
              <span
                className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${bookCategoryColors[book.category]}`}
              >
                {bookCategoryLabels[book.category]}
              </span>
              {book.rating && (
                <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                  <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  {book.rating.toFixed(1)}
                </span>
              )}
              {book.page_count && (
                <span className="text-sm text-muted-foreground">{book.page_count} pages</span>
              )}
              {book.publish_year && (
                <span className="text-sm text-muted-foreground">{book.publish_year}</span>
              )}
            </div>

            <p className="text-sm text-muted-foreground leading-relaxed">{book.description}</p>
          </div>
        </div>

        {/* Content Modules */}
        <div className="space-y-3">
          <h2 className="text-lg font-semibold">Content</h2>

          {allModules.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                Content is being generated. Check back soon.
              </CardContent>
            </Card>
          ) : (
            allModules.map((mod) => {
              const isExpanded = expandedModule === mod.id
              const moduleLabel =
                mod.module_type && bookModuleTypeLabels[mod.module_type]
                  ? bookModuleTypeLabels[mod.module_type]
                  : mod.title

              return (
                <Card key={mod.id} className="overflow-hidden">
                  <button
                    type="button"
                    className="w-full text-left"
                    onClick={() => setExpandedModule(isExpanded ? null : mod.id)}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <CardTitle className="text-base">{moduleLabel}</CardTitle>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {mod.estimated_minutes} min
                          </span>
                          <ChevronRight
                            className={`h-4 w-4 text-muted-foreground transition-transform ${
                              isExpanded ? 'rotate-90' : ''
                            }`}
                          />
                        </div>
                      </div>
                      <CardDescription>{mod.description}</CardDescription>
                    </CardHeader>
                  </button>

                  {isExpanded && (
                    <CardContent className="pt-0 border-t">
                      <div className="pt-4">
                        <ModuleContent moduleData={mod} />
                      </div>
                    </CardContent>
                  )}
                </Card>
              )
            })
          )}

          {/* Personalized Career Application */}
          <Card className="border-primary/30 bg-primary/5">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <User className="h-5 w-5 text-primary" />
                  <CardTitle className="text-base">Career Application</CardTitle>
                </div>
                {book.has_personalized && book.personalized_module ? (
                  <span className="text-xs text-green-600 font-medium">Generated</span>
                ) : isGenerating ? (
                  <span className="text-xs text-primary font-medium flex items-center gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Generating...
                  </span>
                ) : null}
              </div>
              <CardDescription>
                Personalized to your career goals -- how to apply this book's insights to your
                target role
              </CardDescription>
            </CardHeader>
            <CardContent>
              {book.has_personalized && book.personalized_module ? (
                <ModuleContent moduleData={book.personalized_module} />
              ) : isGenerating ? (
                <div className="text-center py-6">
                  <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-3" />
                  <p className="text-sm font-medium mb-1">
                    Generating your personalized insights...
                  </p>
                  <p className="text-xs text-muted-foreground">This usually takes 15-30 seconds</p>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-sm text-muted-foreground mb-3">
                    Generate personalized career insights based on your profile
                  </p>
                  <Button
                    onClick={handlePersonalize}
                    disabled={personalize.isPending}
                    className="gap-2"
                  >
                    <Sparkles className="h-4 w-4" />
                    Generate Career Application
                  </Button>
                  {personalize.isError && (
                    <p className="text-sm text-destructive mt-2">
                      Failed to generate. Please try again.
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Q&A Panel - Ask questions about book content */}
      {activeModuleForQA && (
        <QAPanel
          moduleId={activeModuleForQA.id}
          moduleTitle={`${book.title} - ${
            activeModuleForQA.module_type && bookModuleTypeLabels[activeModuleForQA.module_type]
              ? bookModuleTypeLabels[activeModuleForQA.module_type]
              : activeModuleForQA.title
          }`}
          getScrollPercent={getScrollPercent}
          isOpen={qaOpen}
          onToggle={() => setQaOpen((prev) => !prev)}
        />
      )}
    </div>
  )
}
